/**
 * Authentication Helper Module
 * Manages JWT token-based authentication with localStorage
 */

const AUTH_CONFIG = {
  TOKEN_KEY: 'auth_token',
  USER_KEY: 'user_info',
  LAST_ACTIVITY_KEY: 'auth_last_activity',
  LOGIN_URL: '/api/v1/auth/login',
  RENEW_URL: '/api/v1/auth/session/renew',
  LOGIN_PAGE: '/login.html',
  HOME_PAGE: '/index.html',
  // Otomatik logout ayarları (dakika cinsinden)
  SESSION_TIMEOUT_MIN: 30,    // 30 dakika hareketsizlik sonrası logout
  WARNING_BEFORE_MIN: 2,      // Logout'tan 2 dakika önce uyarı göster
  CHECK_INTERVAL_SEC: 30      // Her 30 saniyede bir kontrol et
};

const AUTH_ROLE_LEVEL = Object.freeze({
  user: 10,
  operator: 20,
  admin: 30,
  super_admin: 40
});

function normalizeUserRole(value) {
  const role = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (role === 'superadmin' || role === 'root') return 'super_admin';
  if (role === 'administrator') return 'admin';
  return Object.prototype.hasOwnProperty.call(AUTH_ROLE_LEVEL, role) ? role : null;
}

function legacyNamedRoleFromGroupPermission(value) {
  const permission = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (permission === 'superadmin' || permission === 'super_admin' || permission === 'root') return 'super_admin';
  if (permission === 'admin' || permission === 'administrator') return 'admin';
  if (permission === 'operator') return 'operator';
  return null;
}

function getEffectiveRole(user = getUserInfo()) {
  if (!user || typeof user !== 'object') return 'user';

  const roleValue = user.Role ?? user.role;
  if (roleValue !== undefined && roleValue !== null && String(roleValue).trim() !== '') {
    return normalizeUserRole(roleValue) || 'user';
  }

  // Named permissions are supported for old deployments. Numeric values are
  // tree/data scope identifiers and must never grant an application role.
  return legacyNamedRoleFromGroupPermission(user.GroupPermission ?? user.group_permission) || 'user';
}

function hasRole(minimumRole, user = getUserInfo()) {
  const required = normalizeUserRole(minimumRole);
  if (!required) return false;
  return AUTH_ROLE_LEVEL[getEffectiveRole(user)] >= AUTH_ROLE_LEVEL[required];
}

// Popup monitor modunda mı? (?popup=1 parametresi)
const _isPopupMode = (typeof URLSearchParams !== 'undefined') &&
  new URLSearchParams(window.location.search).get('popup') === '1';

let _securityConfigurationWarningShown = false;
let _sessionTerminationStarted = false;
let _nativeFetch = null;
let _sessionRenewalPromise = null;

function notifySessionRenewed() {
  if (typeof window.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent('datarapor:session-renewed'));
  }
}

function isLoginPage() {
  return window.location.pathname.endsWith('login.html');
}

function rememberCurrentPage() {
  if (isLoginPage()) return;
  sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
}

function requestServerLogout() {
  try {
    const transport = _nativeFetch || window.fetch.bind(window);
    transport('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true
    }).catch(() => {});
  } catch (error) {
    console.warn('[Auth] Server logout request could not be sent:', error);
  }
}

/**
 * Finish an invalid/expired session exactly once and leave no half-authenticated UI behind.
 */
function terminateSession(reason = 'session_expired', { rememberPage = true } = {}) {
  if (_sessionTerminationStarted) return false;
  _sessionTerminationStarted = true;

  if (rememberPage) rememberCurrentPage();
  // Prevent the login page from restoring the cookie while the keepalive logout
  // request is still being completed by the browser.
  sessionStorage.setItem('skip_sso_restore', '1');
  if (reason !== 'manual_logout') {
    sessionStorage.setItem('session_timeout', '1');
  }

  try {
    stopSessionTimer();
  } catch (error) {
    // The timer may not be initialized yet during very early page startup.
  }

  requestServerLogout();
  clearCredentials();

  if (typeof window.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent('datarapor:session-ended', { detail: { reason } }));
  }

  if (!isLoginPage()) {
    window.location.href = AUTH_CONFIG.LOGIN_PAGE;
  }
  return true;
}

/**
 * Explain the fail-closed JWT response in a way that operators can act on.
 * The warning is intentionally shown once per page load so polling requests
 * cannot repeatedly interrupt the user.
 */
function showSecurityConfigurationWarning() {
  if (_securityConfigurationWarningShown) return;
  _securityConfigurationWarningShown = true;

  const message = 'Güvenlik ayarları yapılmadı.\n\n'
    + 'Sunucuda JWT doğrulama anahtarı yapılandırılmadığı için veriler '
    + 'güvenlik amacıyla yüklenemiyor. Lütfen sistem yöneticinize başvurun.';

  console.error('[Auth] JWT verification is not configured.');
  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    window.alert(message);
  }
}

function notifyIfSecurityConfigurationUnavailable(response) {
  if (!response || response.status !== 503 || typeof response.clone !== 'function') return;

  response.clone().json()
    .then(payload => {
      if (payload && payload.error === 'authentication_unavailable') {
        showSecurityConfigurationWarning();
      }
    })
    .catch(() => {});
}

/**
 * Decode JWT token without verification (client-side)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (e) {
    console.error('JWT decode error:', e);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
function isTokenExpired(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Get stored authentication token
 * @returns {string|null} Token or null if not found/expired
 */
function getToken() {
  const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    terminateSession('token_expired');
    return null;
  }
  
  return token;
}

/**
 * Get stored user information
 * @returns {object|null} User info or null
 */
function getUserInfo() {
  const userJson = localStorage.getItem(AUTH_CONFIG.USER_KEY);
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson);
  } catch (e) {
    console.error('Failed to parse user info:', e);
    return null;
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if valid token exists
 */
function isAuthenticated() {
  return getToken() !== null;
}

/**
 * Login with username and password
 * @param {string} username - Username
 * @param {string} password - Password
 * @returns {Promise<object>} Login response with token and user info
 */
async function login(username, password) {
  try {
    const response = await fetch(AUTH_CONFIG.LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    
    // Check if login was successful
    // Support APIs that return {success: true, token, user} or just {token, user}
    if (data.success === false) {
      throw new Error(data.message || 'Login failed');
    }
    
    // Expected response: { success: true, token: "jwt...", user: {...} }
    if (!data.token) {
      throw new Error('No token received from server');
    }

    acceptLoginResponse(data);

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/** Store a verified login response returned by password or OAuth login. */
function acceptLoginResponse(data, { syncSharedSession = true } = {}) {
    if (!data || !data.token) throw new Error('No token received from server');
    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, data.token);
    if (data.user) {
      const u = data.user;
      // Support both PascalCase (NexBus API) and snake_case field names
      const userInfo = {
        UserId: u.UserId || u.user_id || u.userId || u.sub,
        UserName: u.UserName || u.username || u.userName,
        FirstName: u.FirstName || u.first_name || u.firstName,
        LastName: u.LastName || u.last_name || u.lastName,
        Email: u.Email || u.email,
        GroupPermission: u.GroupPermission ?? u.group_permission ?? u.groupPermission,
        DefaultPageParent: u.DefaultPageParent ?? u.default_page_parent,
        Role: u.Role ?? u.role ?? null
      };
      localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(userInfo));
      
      // Store default page for redirect after login
      const defPage = userInfo.DefaultPageParent;
      if (defPage) {
        sessionStorage.setItem('default_page_parent', defPage);
      }
    }

    // Password logins receive the cookie directly from the server. This call
    // also covers OAuth logins, whose token is delivered to the browser first.
    if (syncSharedSession) {
      fetch('/api/v1/auth/session', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true
      }).catch(() => {});
    }

    notifySessionRenewed();
    return data;
}

/**
 * Clear stored credentials without redirecting (internal use)
 */
function clearCredentials() {
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  localStorage.removeItem(AUTH_CONFIG.LAST_ACTIVITY_KEY);
  sessionStorage.removeItem('default_page_parent');
}

/** Restore this site's local token from the shared, HttpOnly DataRapor session. */
async function restoreSharedSession() {
  try {
    const response = await fetch('/api/v1/auth/session', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;

    const data = await response.json();
    if (!data?.token) return null;
    acceptLoginResponse(data);
    return data;
  } catch (error) {
    console.warn('[Auth] Shared session could not be restored:', error);
    return null;
  }
}

/** Renew a still-valid session on the server and store the returned token. */
async function renewSession() {
  if (_sessionRenewalPromise) return _sessionRenewalPromise;
  _sessionRenewalPromise = (async () => {
    const token = getToken();
    if (!token || _sessionTerminationStarted) {
      terminateSession('session_missing');
      throw new Error('No authentication token available');
    }

    const response = await fetch(AUTH_CONFIG.RENEW_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    const data = await response.clone().json().catch(() => null);
    if (_sessionTerminationStarted) throw new Error('Session already ended');
    if (localStorage.getItem(AUTH_CONFIG.TOKEN_KEY) !== token) {
      throw new Error('Session changed during renewal');
    }

    if (!response.ok || !data?.token) {
      if (response.status === 401 || response.status === 409) {
        terminateSession(data?.error || 'session_renewal_failed');
      }
      throw new Error(data?.message || 'Oturum uzatılamadı.');
    }

    if (data.session_policy) SessionTimer.configure(data.session_policy);
    acceptLoginResponse(data, { syncSharedSession: false });
    return data;
  })().finally(() => { _sessionRenewalPromise = null; });
  return _sessionRenewalPromise;
}

/** Keep Socket.IO authentication current without dropping live subscriptions. */
function bindSessionSocket(socket) {
  if (!socket || socket.__dataRaporSessionBound) return socket;
  socket.__dataRaporSessionBound = true;
  let reconnectAfterExpiry = false;
  const updateCredentials = () => {
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    socket.auth = { ...(socket.auth || {}), token };
    return token;
  };
  const refreshConnection = () => {
    if (_sessionTerminationStarted) return;
    const token = updateCredentials();
    if (!token || isTokenExpired(token)) return;
    if (!socket.connected) {
      socket.connect();
      return;
    }
    socket.timeout(5000).emit('auth:refresh', { token }, (error, result) => {
      if (_sessionTerminationStarted || localStorage.getItem(AUTH_CONFIG.TOKEN_KEY) !== token) return;
      // Reconnect also supports older servers that do not handle auth:refresh.
      // The page's existing connect handler restores its tag subscriptions.
      if (error || !result?.success) {
        socket.disconnect();
        socket.connect();
      }
    });
  };
  updateCredentials();
  if (socket.io && typeof socket.io.on === 'function') {
    socket.io.on('reconnect_attempt', updateCredentials);
  }
  socket.on('auth:expired', () => {
    const token = updateCredentials();
    reconnectAfterExpiry = !_sessionTerminationStarted && Boolean(token) && !isTokenExpired(token);
  });
  socket.on('disconnect', () => {
    if (reconnectAfterExpiry && !_sessionTerminationStarted) {
      reconnectAfterExpiry = false;
      socket.connect();
    }
  });
  window.addEventListener('datarapor:session-renewed', refreshConnection);
  window.addEventListener('datarapor:session-ended', () => socket.disconnect());
  return socket;
}

/**
 * Logout - clear stored credentials and redirect to login page
 */
function logout() {
  terminateSession('manual_logout', { rememberPage: false });
}

/**
 * Redirect to login page if not authenticated
 * Call this on protected pages
 */
function requireAuth() {
  if (!isAuthenticated()) {
    if (_sessionTerminationStarted) return false;
    // Store current page to redirect back after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirect_after_login', currentPath);
    
    window.location.href = `${AUTH_CONFIG.LOGIN_PAGE}?sso=1`;
    return false;
  }
  return true;
}

/**
 * Redirect to home page if already authenticated
 * Call this on login page
 */
function redirectIfAuthenticated() {
  if (isAuthenticated()) {
    const redirectTo = sessionStorage.getItem('redirect_after_login') || AUTH_CONFIG.HOME_PAGE;
    sessionStorage.removeItem('redirect_after_login');
    window.location.href = redirectTo;
    return true;
  }
  return false;
}

/**
 * Create fetch wrapper with automatic token injection
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
async function authenticatedFetch(url, options = {}) {
  const token = getToken();
  
  if (!token) {
    terminateSession('session_missing');
    throw new Error('No authentication token available');
  }

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };

  const response = await fetch(url, {
    ...options,
    headers
  });

  // Auto logout on 401 Unauthorized
  if (response.status === 401) {
    if (localStorage.getItem(AUTH_CONFIG.TOKEN_KEY) === token) terminateSession('unauthorized');
    throw new Error('Session expired');
  }

  return response;
}

/**
 * Add the current Bearer token to every same-origin API and runtime data request.
 * This covers `/api/*`, `/admin/api/*`, and authenticated data JSON files so
 * individual pages cannot accidentally bypass authentication with raw fetch().
 */
function installFetchInterceptor() {
  if (window.fetch.__dataRaporAuthInterceptor) return;

  const nativeFetch = window.fetch.bind(window);
  _nativeFetch = nativeFetch;
  const interceptedFetch = function(input, options = {}) {
    let shouldHandleUnauthorized = false;
    let requestToken = null;
    try {
      const rawUrl = typeof input === 'string'
        ? input
        : (input && typeof input.url === 'string' ? input.url : '');
      const parsed = new URL(rawUrl, window.location.origin);
      const isProtectedResource = parsed.origin === window.location.origin
        && (
          /^\/api(?:\/|$)/.test(parsed.pathname)
          || /^\/admin\/api(?:\/|$)/.test(parsed.pathname)
          || /^\/assets\/data(?:\/|$)/.test(parsed.pathname)
          || /^\/Report\/assets\/data(?:\/|$)/.test(parsed.pathname)
        );
      const method = String(options.method
        || (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET'))
        .toUpperCase();
      const isPublicAuthRequest = (method === 'POST' && /^\/api\/v1\/auth\/login\/?$/.test(parsed.pathname))
        || (method === 'GET' && /^\/api\/v1\/auth\/session\/?$/.test(parsed.pathname))
        || (method === 'POST' && /^\/api\/v1\/auth\/logout\/?$/.test(parsed.pathname))
        || (method === 'GET' && /^\/api\/v1\/auth\/microsoft\/(?:config|start|callback|complete)\/?$/.test(parsed.pathname));
      shouldHandleUnauthorized = isProtectedResource && !isPublicAuthRequest;
      const token = isProtectedResource ? getToken() : null;
      requestToken = token;

      if (token) {
        const inheritedHeaders = options.headers
          || (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined);
        const headers = new Headers(inheritedHeaders || {});
        headers.set('Authorization', `Bearer ${token}`);
        options = { ...options, headers };
      }
    } catch (error) {
      console.warn('[Auth] API request interceptor warning:', error);
    }
    return nativeFetch(input, options).then(response => {
      notifyIfSecurityConfigurationUnavailable(response);
      if (response.status === 401 && shouldHandleUnauthorized
        && localStorage.getItem(AUTH_CONFIG.TOKEN_KEY) === requestToken) {
        terminateSession('unauthorized');
      }
      return response;
    });
  };

  interceptedFetch.__dataRaporAuthInterceptor = true;
  window.fetch = interceptedFetch;
}

/**
 * PLC yazma yetkisini kontrol eder.
 * `user` rolü yazamaz; `operator`, `admin` ve `super_admin` yazabilir.
 */
function canWritePlc() {
  try {
    return hasRole('operator');
  } catch (e) {
    return false;
  }
}

// Export functions for global use
window.Auth = {
  login,
  acceptLoginResponse,
  logout,
  isAuthenticated,
  getToken,
  getUserInfo,
  getEffectiveRole,
  hasRole,
  restoreSharedSession,
  renewSession,
  bindSessionSocket,
  requireAuth,
  redirectIfAuthenticated,
  authenticatedFetch,
  startSessionTimer,
  stopSessionTimer,
  canWritePlc
};

installFetchInterceptor();

// A logout/expiry in one tab must not leave another tab looking authenticated.
if (typeof window.addEventListener === 'function') {
  window.addEventListener('storage', event => {
    if (event.key === AUTH_CONFIG.TOKEN_KEY && event.oldValue && !event.newValue && !isLoginPage()) {
      terminateSession('session_ended_in_another_tab', { rememberPage: false });
    } else if (event.key === AUTH_CONFIG.TOKEN_KEY && event.newValue && event.newValue !== event.oldValue) {
      notifySessionRenewed();
    } else if (event.key === AUTH_CONFIG.LAST_ACTIVITY_KEY && event.newValue) {
      try {
        SessionTimer.syncActivity(Number(event.newValue));
      } catch (error) {
        // SessionTimer may still be initializing during very early page startup.
      }
    }
  });
}

// ===========================
// Otomatik Logout (Inaktivite Tabanlı)
// ===========================

const SessionTimer = (function() {
  let _lastActivity = Date.now();
  let _checkInterval = null;
  let _warningShown = false;
  let _warningModal = null;
  let _countdownInterval = null;
  let _warningDeadline = 0;
  let _warningReason = 'idle_timeout';
  let _renewalPending = false;
  let _inactivityTimeoutEnabled = true;
  let _automaticRenewalEnabled = false;
  let _automaticRenewalPending = false;

  let TIMEOUT_MS = AUTH_CONFIG.SESSION_TIMEOUT_MIN * 60 * 1000;
  let WARNING_MS = AUTH_CONFIG.WARNING_BEFORE_MIN * 60 * 1000;
  const CHECK_MS = AUTH_CONFIG.CHECK_INTERVAL_SEC * 1000;

  function _resetActivity() {
    // Once the warning is visible, continuing requires an explicit server check.
    if (_warningShown) return;
    _lastActivity = Date.now();
    localStorage.setItem(AUTH_CONFIG.LAST_ACTIVITY_KEY, String(_lastActivity));
  }

  function _confirmActivity() {
    _lastActivity = Date.now();
    localStorage.setItem(AUTH_CONFIG.LAST_ACTIVITY_KEY, String(_lastActivity));
    _hideWarning();
  }

  function _syncActivity(value) {
    if (!Number.isFinite(value) || value <= _lastActivity) return;
    _lastActivity = value;
    if (_warningShown) _hideWarning();
  }

  const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

  function _throttle(fn, ms) {
    let last = 0;
    return function() {
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        fn();
      }
    };
  }

  const _throttledReset = _throttle(_resetActivity, 5000); // 5 saniyede bir güncelle

  function _attachListeners() {
    ACTIVITY_EVENTS.forEach(evt => {
      document.addEventListener(evt, _throttledReset, { passive: true });
    });
  }

  function _detachListeners() {
    ACTIVITY_EVENTS.forEach(evt => {
      document.removeEventListener(evt, _throttledReset);
    });
  }

  function _createWarningModal() {
    if (document.getElementById('sessionTimeoutModal')) return;

    const modalHTML = `
      <div id="sessionTimeoutModal" class="modal fade" tabindex="-1" data-coreui-backdrop="static" data-coreui-keyboard="false">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header bg-warning text-dark">
              <h5 class="modal-title">
                <svg class="icon me-2" style="width:20px;height:20px"><use xlink:href="vendors/@coreui/icons/svg/free.svg#cil-warning"></use></svg>
                Oturum Zaman Aşımı
              </h5>
            </div>
            <div class="modal-body text-center">
              <p class="mb-2" id="sessionWarningMessage">Uzun süredir işlem yapmadınız.</p>
              <p class="mb-3" role="status" aria-live="polite">Oturumunuz <strong id="sessionCountdown">--</strong> saniye sonra sonlandırılacak.</p>
              <p class="small text-danger d-none" id="sessionRenewError" role="alert"></p>
            </div>
            <div class="modal-footer justify-content-center">
              <button type="button" class="btn btn-outline-secondary px-4" id="sessionLogoutBtn">Çıkış Yap</button>
              <button type="button" class="btn btn-primary px-4" id="sessionKeepAliveBtn">Oturumu Devam Ettir</button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    document.getElementById('sessionLogoutBtn').addEventListener('click', function() {
      logout();
    });

    document.getElementById('sessionKeepAliveBtn').addEventListener('click', async function() {
      if (_renewalPending) return;
      _renewalPending = true;
      const button = this;
      const errorEl = document.getElementById('sessionRenewError');
      button.disabled = true;
      button.textContent = 'Oturum kontrol ediliyor...';
      if (errorEl) errorEl.classList.add('d-none');

      try {
        await renewSession();
        if (!_sessionTerminationStarted) _confirmActivity();
      } catch (error) {
        if (!_sessionTerminationStarted && errorEl) {
          errorEl.textContent = error.message || 'Oturum doğrulanamadı. Lütfen tekrar deneyin.';
          errorEl.classList.remove('d-none');
        }
      } finally {
        _renewalPending = false;
        button.disabled = false;
        button.textContent = 'Oturumu Devam Ettir';
      }
    });
  }

  function _showWarning(deadline, reason) {
    if (_warningShown) {
      if (deadline < _warningDeadline) {
        _warningDeadline = deadline;
        _warningReason = reason;
      }
      return;
    }
    _warningShown = true;
    _warningDeadline = deadline;
    _warningReason = reason;
    _createWarningModal();

    const modalEl = document.getElementById('sessionTimeoutModal');
    if (!modalEl) return;
    const warningMessage = document.getElementById('sessionWarningMessage');
    if (warningMessage) {
      warningMessage.textContent = reason === 'token_expired'
        ? 'Oturumunuzun güvenlik süresi dolmak üzere.'
        : 'Uzun süredir işlem yapmadınız.';
    }

    // CoreUI/Bootstrap Modal
    if (window.coreui && window.coreui.Modal) {
      _warningModal = new coreui.Modal(modalEl);
    } else if (window.bootstrap && window.bootstrap.Modal) {
      _warningModal = new bootstrap.Modal(modalEl);
    } else {
      // Fallback: doğrudan göster
      modalEl.classList.add('show');
      modalEl.style.display = 'block';
      modalEl.removeAttribute('aria-hidden');
    }

    if (_warningModal) _warningModal.show();

    const countdownEl = document.getElementById('sessionCountdown');
    const updateCountdown = function() {
      const remaining = Math.max(0, Math.ceil((_warningDeadline - Date.now()) / 1000));
      if (countdownEl) countdownEl.textContent = remaining;
      if (remaining === 0 && !_renewalPending) {
        clearInterval(_countdownInterval);
        _countdownInterval = null;
        terminateSession(_warningReason);
      }
    };

    updateCountdown();
    if (!_sessionTerminationStarted) {
      _countdownInterval = setInterval(updateCountdown, 1000);
    }
  }

  function _hideWarning() {
    _warningShown = false;
    _warningDeadline = 0;
    _warningReason = 'idle_timeout';
    if (_countdownInterval) {
      clearInterval(_countdownInterval);
      _countdownInterval = null;
    }
    if (_warningModal) {
      _warningModal.hide();
      _warningModal = null;
    } else {
      const modalEl = document.getElementById('sessionTimeoutModal');
      if (modalEl) {
        modalEl.classList.remove('show');
        modalEl.style.display = 'none';
      }
    }
  }

  function _check() {
    if (isLoginPage() || _sessionTerminationStarted) return;

    // Token yoksa zaten çıkmış
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    if (!token) {
      stop();
      terminateSession('session_missing');
      return;
    }

    _syncActivity(Number(localStorage.getItem(AUTH_CONFIG.LAST_ACTIVITY_KEY)));

    const decoded = decodeJWT(token);
    if (!decoded?.exp) {
      terminateSession('token_invalid');
      return;
    }

    const now = Date.now();
    const idleDeadline = _inactivityTimeoutEnabled
      ? _lastActivity + TIMEOUT_MS
      : Number.POSITIVE_INFINITY;
    const tokenDeadline = decoded.exp * 1000;
    const deadline = Math.min(idleDeadline, tokenDeadline);
    const reason = tokenDeadline <= idleDeadline ? 'token_expired' : 'idle_timeout';
    const remaining = deadline - now;

    if (remaining <= 0) {
      terminateSession(reason);
      return;
    }

    if (_automaticRenewalEnabled && tokenDeadline - now <= AUTH_CONFIG.WARNING_BEFORE_MIN * 60 * 1000) {
      if (!_automaticRenewalPending) {
        _automaticRenewalPending = true;
        renewSession().then(() => {
          if (!_sessionTerminationStarted) _hideWarning();
        }).catch(error => {
          // Retry transient failures on the next check while the token is valid.
          console.warn('[Auth] Otomatik oturum yenileme başarısız:', error.message);
        }).finally(() => { _automaticRenewalPending = false; });
      }
      return;
    }

    if (remaining <= WARNING_MS) {
      _showWarning(deadline, reason);
    } else if (_warningShown) {
      _hideWarning();
    }
  }

  function setTimeout(minutes) {
    if (minutes && minutes >= 5 && minutes <= 480) {
      TIMEOUT_MS = minutes * 60 * 1000;
      WARNING_MS = Math.min(AUTH_CONFIG.WARNING_BEFORE_MIN * 60 * 1000, TIMEOUT_MS / 2);
      console.log(`[Auth] Oturum timeout güncellendi: ${minutes} dk`);
    }
  }

  function configure(config) {
    _automaticRenewalEnabled = config?.sessionTimeoutEnabled === false;
    _inactivityTimeoutEnabled = !_isPopupMode && !_automaticRenewalEnabled;
    setTimeout(config?.sessionTimeoutMin);
  }

  function start({ inactivityTimeout = true, automaticRenewal = false } = {}) {
    if (_checkInterval) return; // Zaten çalışıyor
    _inactivityTimeoutEnabled = inactivityTimeout;
    _automaticRenewalEnabled = automaticRenewal;
    _lastActivity = Date.now();
    localStorage.setItem(AUTH_CONFIG.LAST_ACTIVITY_KEY, String(_lastActivity));
    _attachListeners();
    _check();
    if (_sessionTerminationStarted) return;
    _checkInterval = setInterval(_check, CHECK_MS);
    console.log(_inactivityTimeoutEnabled
      ? `[Auth] Oturum zamanlayıcısı başlatıldı (${Math.round(TIMEOUT_MS / 60000)} dk timeout)`
      : '[Auth] Oturum zamanlayıcısı başlatıldı (hareketsizlik nedeniyle kapanma kapalı)');
  }

  function stop() {
    _detachListeners();
    if (_checkInterval) {
      clearInterval(_checkInterval);
      _checkInterval = null;
    }
    _hideWarning();
  }

  return { start, stop, setTimeout, configure, syncActivity: _syncActivity };
})();

function startSessionTimer(options) {
  SessionTimer.start(options);
}

function stopSessionTimer() {
  SessionTimer.stop();
}

// Sayfa yüklendiğinde, giriş yapmışsa zamanlayıcıyı otomatik başlat
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', function() {
    // Login sayfasında başlatma
    if (window.location.pathname.endsWith('login.html')) {
      // Timeout sonrası login sayfasına düştüyse mesaj göster
      if (sessionStorage.getItem('session_timeout') === '1') {
        sessionStorage.removeItem('session_timeout');
        setTimeout(function() {
          const errorDiv = document.getElementById('loginError');
          if (errorDiv) {
            errorDiv.textContent = 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.';
            errorDiv.classList.remove('d-none');
          }
        }, 100);
      }
      return;
    }

    // Token varsa oturum zamanlayıcısını başlat
    if (localStorage.getItem(AUTH_CONFIG.TOKEN_KEY)) {
      // Hareketsizlik ayarını oku; kapalı olsa da token süresini izlemeye devam et.
      fetch('/api/ui-settings/menu').then(r => r.ok ? r.json() : null).then(cfg => {
        if (cfg && cfg.sessionTimeoutMin) {
          SessionTimer.setTimeout(cfg.sessionTimeoutMin);
        }
        const persistentSession = cfg?.sessionTimeoutEnabled === false;
        startSessionTimer({
          inactivityTimeout: !_isPopupMode && !persistentSession,
          automaticRenewal: persistentSession
        });
      }).catch(() => {
        startSessionTimer({ inactivityTimeout: !_isPopupMode }); // Config okunamazsa mevcut davranışı koru
      });
    }
  });
}
